import { useState, useEffect, useContext } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { color, Point } from 'highcharts';
import "highcharts/modules/accessibility";

import { Grid2 as Grid, Avatar, Box, Card, CardContent, CardHeader, Typography, Stack, Alert, FormControl, Select, MenuItem } from '@mui/material';
import { amber, blue } from '@mui/material/colors';
import { FilersDataContext } from '../../../Filers';
import BarChartIcon from '@mui/icons-material/BarChart';
import RaceActivityReport from '../../../../../types/reports/raceActivityReport';

const RaceActivitiesChart = (): JSX.Element => {

    const filerDataContext = useContext(FilersDataContext);
    const currentFiler = filerDataContext.currentFilerPayload?.filer;
    const raceActivityReports = filerDataContext.currentFilerPayload?.raceActivityReports;
    
    const [chartNames, setChartNames] = useState<string[]>();
    const [chartValues, setChartValues] = useState<number[]>();
    const [noActivity, setNoActivity] = useState<boolean>(true);
    const [barHeight, setBarHeight] = useState<string>('30vh');
    const [raceName, setRaceName] = useState<string>('Race');
    const [chartTypeLabel, setChartTypeLabel] = useState<string>('Total Contributions');

    const chartColors = [
      '#76b7b2',
      '#59a14f',
      '#f28e2c',
      '#4db6ac',
      '#e15759',
      '#edc949',
      '#4e79a7',
      '#af7aa1',
      '#ff9da7',
      '#9c755f',
      '#bab0ab',
    ];

    const RaceChartOptions = {
      chart: {
          type: 'bar',
      },
      title: {
        text: raceName,
        style: {
          color: 'blue',
          fontWeight: '550'
        }
      },
      tooltip: {
          valueDecimals: 2,
          valuePrefix: '$'
      },
      xAxis: {
          categories: chartNames,
          title: {
              text: null
          },
          labels: {
              style: {
                  fontSize: '13px',
                  color: 'black',
                  fontWeight: '550'
              },
          },
          min: 0,
          lineWidth: 0,
          gridLineWidth: 0,
          lineColor: 'transparent',
          minorTickLength: 0,
          tickLength: 0
      },
      yAxis: {
          min: 0,
          title: {
              text: chartTypeLabel,
              align: 'high'
          },
          labels: {
              overflow: 'justify'
          },
          lineWidth: 0,
          minorGridLineWidth: 0,
          lineColor: 'transparent'
      },
      plotOptions: {
          bar: {
              dataLabels: {
                  enabled: true,
                  style: {
                      fontWeight: 'bold'
                  },
                  format: '${point.y:,.2f}'
              },
              cursor: 'pointer',
              colorByPoint: true,
              point: {
                  events: {
                      //click: updateGridData
                  }
              }
          }
      },
      legend: {
          enabled: false
      },
      credits: {
          enabled: false
      },
      exporting: {
          enabled: false
      },
      series: [
          {
              name: chartTypeLabel,
              data: chartValues
          }
      ]
    };


    useEffect(() => {
      if (raceActivityReports && raceActivityReports?.length>0) {
        setChartNames(Object.values(raceActivityReports).map((item: RaceActivityReport) => item.filerName));
        setChartValues(Object.values(raceActivityReports).map((item: RaceActivityReport) => item.receiptsYtdNumeric));
        let raceName = Object.values(raceActivityReports)[0].officeSought;
        let raceYear = new Date(Object.values(raceActivityReports)[0].reportYearFirstDayDate).getFullYear();
        setRaceName(`${raceYear} ${raceName} Race`);
        setNoActivity(false);
        if(Object.values(raceActivityReports).length >= 4){
          setBarHeight('80vh');
        }
        else{
          setBarHeight('30vh');
        }
      }
      else{
        setNoActivity(true);
        setBarHeight('10vh');
      }
    }, [currentFiler])


const updateChartType = (event: any) => {

    const value = event.target.value as string;
    if(raceActivityReports){
      if(value === "raised"){
        setChartValues(Object.values(raceActivityReports).map((item: RaceActivityReport) => item.receiptsYtdNumeric));
        setChartTypeLabel('Total Contributions');
      }
      else{
        setChartValues(Object.values(raceActivityReports).map((item: RaceActivityReport) => item.expendituresYtdNumeric));
        setChartTypeLabel('Total Expenditures');
      }
    }
}

    return (
        <Grid size={{ xs: 12, md: 6.5 }} marginTop={2} data-aos={'fade-up'}>
          <Card 
            sx={{
              "&:hover": {
                boxShadow: "0 0 10px #ccc",
              },
              borderTop: `2px solid`,
              borderTopColor: amber[700],
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              height: '100%',
            }}
          >
            <CardHeader
                avatar={
                <Avatar sx={{ bgcolor: blue[500] }} className="small-avatar">
                    <BarChartIcon fontSize="small" />
                </Avatar>}
                padding={1}
                title={
                    <Typography
                      variant="h6">
                      Compare to Opponents
                    </Typography>
                }                                                      
            />                                                   
            <CardContent className="card-content">
                <Grid container 
                spacing={1}
                marginBottom={4}
                style={{
                  height: barHeight,
                }}
                >
                  {
                    noActivity?
                    <Box>
                        <Alert icon={false} severity="warning">
                          There is no opponents information available.
                        </Alert>
                    </Box>:
                    <>
                     <Stack direction="row" spacing={2}>
                      <FormControl sx={{ minWidth: 120}} size="small">
                        <Select 
                          defaultValue={"raised"}
                          sx={{fontSize: "smaller", padding: 0.2}}
                          onChange={updateChartType}
                        >
                          <MenuItem value={"raised"}>Total Raised</MenuItem>
                          <MenuItem value={"spent"}>Total Spent</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                    <Grid size={12} display={'flex'} justifyContent={'center'} sx={{height: '100%'}}>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={RaceChartOptions}
                      />
                    </Grid>
                    
                    {/* mmaterial x-charts
                    
                    <BarChart
                      series={[
                        { data: chartValues},
                      ]}
                      yAxis={[
                      {
                          data: chartNames,
                          scaleType: 'band',
                          colorMap: {
                              type: "ordinal",
                              values: chartNames,
                              colors: chartColors
                          },
                          categoryGapRatio: 0.45
                      } as AxisConfig<'band', any, ChartsYAxisProps>
                      ]}
                      layout="horizontal"
                      width={650}
                      margin={{ top: 15, bottom: 30, left: 150, right: 10 }}
                      barLabel={(item) => {
                        const formattedAmount = new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                        }).format(item.value ?? 0);
                          return `${formattedAmount}`;
                        }}
                      sx={{ 
                          '.MuiChartsAxis-tickLabel': { 
                              fontWeight: '550 !important',
                          },
                          }}              
                    />

                    */}
                     
                    </>
                  }
                </Grid>  
            </CardContent>                                               
          </Card>
        </Grid>
    );
  };

export default RaceActivitiesChart;